import React, { useState } from 'react';
import { X, ArrowLeft, ArrowRight, Home, Building2, Sparkles, Calendar, Check } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useT } from '../i18n/LanguageContext';
import { createBooking } from '../lib/api';

export default function BookingModal({ open, onClose }) {
  const { toast } = useToast();
  const { t, lang } = useT();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    service: '',
    size: '',
    frequency: '',
    date: '',
    time: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
  });

  if (!open) return null;

  const serviceTypes = [
    { id: 'residential', label: t('book.residential'), icon: Home, desc: t('book.residential.desc') },
    { id: 'commercial', label: t('book.commercial'), icon: Building2, desc: t('book.commercial.desc') },
    { id: 'deep', label: t('book.deep'), icon: Sparkles, desc: t('book.deep.desc') },
    { id: 'endoftenancy', label: t('book.endTen'), icon: Calendar, desc: t('book.endTen.desc') },
  ];

  const frequencies = [t('calc.freq.oneoff'), t('calc.freq.weekly'), t('calc.freq.biweekly'), t('calc.freq.monthly')];
  const sizes = [t('calc.size.1'), t('calc.size.2'), t('calc.size.3'), t('calc.size.4'), t('calc.size.office')];

  const upd = (k, v) => setData({ ...data, [k]: v });
  const upde = (k) => (e) => upd(k, e.target.value);

  const canNext = () => {
    if (step === 1) return !!data.service;
    if (step === 2) return !!data.size && !!data.frequency;
    if (step === 3) return !!data.date;
    return true;
  };

  const next = () => setStep((s) => Math.min(4, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.phone) {
      toast({ title: t('book.toast.miss'), description: t('book.toast.missDesc') });
      return;
    }
    setSubmitting(true);
    try {
      await createBooking({ ...data, language: lang });
      toast({
        title: t('book.toast.ok'),
        description: t('book.toast.okDesc'),
      });
      onClose();
      setTimeout(() => {
        setStep(1);
        setData({ service: '', size: '', frequency: '', date: '', time: '', name: '', phone: '', email: '', address: '', notes: '' });
      }, 400);
    } catch (err) {
      toast({
        title: lang === 'pt' ? 'Erro no envio' : 'Submission error',
        description: lang === 'pt'
          ? 'N\u00e3o foi poss\u00edvel enviar. Tente novamente ou fale conosco no WhatsApp.'
          : 'We couldn\u2019t send your booking. Please try again or reach us on WhatsApp.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#1a2851]/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col uc-fade-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#eef6f4]">
          <div>
            <p className="uc-kicker">{t('book.kicker')}</p>
            <h3 className="font-serif text-2xl font-bold text-[#1a2851]">{t('book.title')}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-[#f2fbf9] flex items-center justify-center text-[#1a2851]">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pt-5">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={`flex-1 h-1.5 rounded-full transition-colors ${n <= step ? 'bg-[#4dbfae]' : 'bg-[#e6f7f4]'}`} />
            ))}
          </div>
          <p className="text-xs text-[#5b6469] mt-2">{t('book.step')} {step} {t('book.of')} 4</p>
        </div>

        <div className="px-6 py-6 overflow-y-auto">
          {step === 1 && (
            <div>
              <h4 className="font-serif text-xl font-bold text-[#1a2851] mb-4">{t('book.step1')}</h4>
              <div className="grid grid-cols-2 gap-3">
                {serviceTypes.map((s) => {
                  const Icon = s.icon;
                  const active = data.service === s.label;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => upd('service', s.label)}
                      className={`text-left p-4 rounded-xl border-2 transition-all ${
                        active ? 'border-[#4dbfae] bg-[#f2fbf9]' : 'border-[#eef6f4] hover:border-[#4dbfae]/50'
                      }`}
                    >
                      <Icon size={22} className={active ? 'text-[#4dbfae]' : 'text-[#1a2851]'} />
                      <p className="font-semibold text-[#1a2851] mt-2">{s.label}</p>
                      <p className="text-xs text-[#5b6469]">{s.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h4 className="font-serif text-xl font-bold text-[#1a2851] mb-4">{t('book.propSize')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {sizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => upd('size', s)}
                      className={`px-3 py-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                        data.size === s ? 'border-[#4dbfae] bg-[#f2fbf9] text-[#1a2851]' : 'border-[#eef6f4] text-[#2b2e33] hover:border-[#4dbfae]/50'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-serif text-xl font-bold text-[#1a2851] mb-4">{t('book.howOften')}</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {frequencies.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => upd('frequency', f)}
                      className={`px-3 py-3 rounded-lg text-sm font-medium border-2 transition-colors ${
                        data.frequency === f ? 'border-[#4dbfae] bg-[#f2fbf9] text-[#1a2851]' : 'border-[#eef6f4] text-[#2b2e33] hover:border-[#4dbfae]/50'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h4 className="font-serif text-xl font-bold text-[#1a2851] mb-4">{t('book.when')}</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#1a2851] mb-1 block">{t('book.date')}</label>
                  <input type="date" value={data.date} onChange={upde('date')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1a2851] mb-1 block">{t('book.time')}</label>
                  <select value={data.time} onChange={upde('time')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20 bg-white">
                    <option value="">{t('book.anyTime')}</option>
                    <option>{t('book.morning')}</option>
                    <option>{t('book.afternoon')}</option>
                    <option>{t('book.evening')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1a2851] mb-1 block">{t('book.address')}</label>
                <input type="text" placeholder={t('book.addressPh')} value={data.address} onChange={upde('address')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1a2851] mb-1 block">{t('book.notes')}</label>
                <textarea rows={3} placeholder={t('book.notesPh')} value={data.notes} onChange={upde('notes')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20 resize-none" />
              </div>
            </div>
          )}

          {step === 4 && (
            <form onSubmit={submit} className="space-y-4">
              <h4 className="font-serif text-xl font-bold text-[#1a2851] mb-2">{t('book.contact')}</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <input type="text" placeholder={t('book.name')} value={data.name} onChange={upde('name')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20" />
                <input type="tel" placeholder={t('book.phone')} value={data.phone} onChange={upde('phone')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20" />
              </div>
              <input type="email" placeholder={t('book.email')} value={data.email} onChange={upde('email')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20" />

              <div className="bg-[#f2fbf9] rounded-xl p-4 border border-[#e6f7f4] text-sm">
                <p className="font-semibold text-[#1a2851] mb-2 flex items-center gap-2">
                  <Check size={16} className="text-[#4dbfae]" /> {t('book.summary')}
                </p>
                <ul className="text-[#5b6469] space-y-1">
                  <li><span className="font-medium text-[#1a2851]">{t('book.service')}</span> {data.service || '—'}</li>
                  <li><span className="font-medium text-[#1a2851]">{t('book.size')}</span> {data.size || '—'}</li>
                  <li><span className="font-medium text-[#1a2851]">{t('book.freq')}</span> {data.frequency || '—'}</li>
                  <li><span className="font-medium text-[#1a2851]">{t('book.dateL')}</span> {data.date || '—'} {data.time && `• ${data.time}`}</li>
                </ul>
              </div>

              <button type="submit" disabled={submitting} className="uc-btn w-full justify-center disabled:opacity-60">
                {submitting ? (lang === 'pt' ? 'Enviando…' : 'Sending…') : t('book.confirm')}
                <ArrowRight size={18} />
              </button>
            </form>
          )}
        </div>

        {step < 4 && (
          <div className="px-6 py-4 border-t border-[#eef6f4] flex justify-between">
            <button onClick={prev} disabled={step === 1} className="uc-btn-outline disabled:opacity-40 disabled:cursor-not-allowed">
              <ArrowLeft size={18} /> {t('book.back')}
            </button>
            <button onClick={next} disabled={!canNext()} className="uc-btn disabled:opacity-40 disabled:cursor-not-allowed">
              {t('book.next')} <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
